import "@/App.css";
import { Toaster } from "@/components/ui/sonner";

// Services
import { isAuthenticated, getCurrentUser, logout } from "@/services/auth";
import { useEffect, useState } from "react";
import { NAV_EVENT, installLinkInterceptor } from "@/services/navigation";

// Pages
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import AcceptInvitePage from "@/pages/auth/AcceptInvitePage";
import AcceptStaffInvitePage from "@/pages/auth/AcceptStaffInvitePage";
import ApplyPage from "@/pages/apply/ApplyPage";
import FixPage from "@/pages/fix/FixPage";
import DemoPage from "@/pages/demo/DemoPage";
import ContactPage from "@/pages/contact/ContactPage";
import OnboardPage from "@/pages/onboard/OnboardPage";
import RoommateIdPage from "@/pages/onboard/RoommateIdPage";
import ReferencePage from "@/pages/reference/ReferencePage";
import LandlordDashboard from "@/pages/landlord/DashboardPage";
import TenantAccessPage from "@/pages/tenant/AccessPage";
import TenantConsentPage from "@/pages/tenant/ConsentPage";
import AdminDashboard from "@/pages/admin/DashboardPage";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import HomePage from "@/pages/HomePage";
import FAQPage from "@/pages/FAQPage";
import RentReportingPage from "@/pages/product/RentReportingPage";
import RentCollectionPage from "@/pages/product/RentCollectionPage";
import ScreeningPage from "@/pages/product/ScreeningPage";
import WaitlistPage from "@/pages/waitlist/WaitlistPage";
import WaitlistActivatePage from "@/pages/waitlist/WaitlistActivatePage";

// Legal Pages
import PrivacyPolicyPage from "@/pages/legal/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/legal/TermsOfServicePage";
import DataProtectionPage from "@/pages/legal/DataProtectionPage";
import AccountDeletionPage from "@/pages/legal/AccountDeletionPage";

// Community Pages
import EcosystemPage from "@/pages/community/EcosystemPage";
import WhitepaperPage from "@/pages/community/WhitepaperPage";
import UnsubscribePage from "@/pages/UnsubscribePage";

// Careers
import CareersPage from "@/pages/careers/CareersPage";
import JobPage from "@/pages/careers/JobPage";
import CandidateLoginPage from "@/pages/careers/CandidateLoginPage";
import CandidatePortalPage from "@/pages/careers/CandidatePortalPage";

/**
 * Tenantra Inc. - MPA Router
 * 
 * This app uses traditional Multi-Page Application navigation.
 * Each route renders a full page component.
 * Navigation between pages triggers full page loads via standard <a href> links.
 * SPA-like behavior is only used for in-page interactions (modals, filters, etc.)
 */

// Route configuration
const routes = {
  // Public routes
  "/": HomePage,
  "/rent-reporting": RentReportingPage,
  "/rent-collection": RentCollectionPage,
  "/screening": ScreeningPage,
  "/login": LoginPage,
  "/register": RegisterPage,
  "/waitlist": WaitlistPage,
  "/waitlist/activate": WaitlistActivatePage,
  "/forgot-password": ForgotPasswordPage,
  "/reset-password": ResetPasswordPage,
  "/invite/accept": AcceptInvitePage,
  
  // Legal pages
  "/privacy-policy": PrivacyPolicyPage,
  "/terms-of-service": TermsOfServicePage,
  "/data-protection": DataProtectionPage,
  "/account-deletion": AccountDeletionPage,
  "/delete-account": AccountDeletionPage,
  "/unsubscribe": UnsubscribePage,
  
  // Community pages
  "/community": EcosystemPage,
  "/ecosystem": EcosystemPage,
  "/whitepaper": WhitepaperPage,
  "/faq": FAQPage,

  // Careers
  "/careers": CareersPage,
  "/careers/login": CandidateLoginPage,
  "/careers/portal": CandidatePortalPage,
  
  // Admin login (separate from regular login)
  "/portal-8k2m9x/login": AdminLoginPage,
  
  // Tenant portal (public with OTP verification)
  "/tenant/access": TenantAccessPage,
  "/tenant/verify": TenantAccessPage,
  "/tenant/report": TenantAccessPage,
  "/tenant/consent": TenantConsentPage,
  
  // Landlord portal (authenticated)
  "/landlord/dashboard": LandlordDashboard,
  "/landlord/properties": LandlordDashboard,
  "/landlord/properties/new": LandlordDashboard,
  "/landlord/tenants": LandlordDashboard,
  "/landlord/tenants/new": LandlordDashboard,
  "/landlord/tenants/:id": LandlordDashboard,
  "/landlord/tenants/:id/end": LandlordDashboard,
  "/landlord/reports": LandlordDashboard,
  "/landlord/reports/new": LandlordDashboard,
  "/landlord/reports/:id/edit": LandlordDashboard,
  "/landlord/reports/:id/new": LandlordDashboard,
  "/landlord/view-report": LandlordDashboard,
  "/landlord/screening": LandlordDashboard,
  "/landlord/disputes": LandlordDashboard,
  "/landlord/disputes": LandlordDashboard,
  "/landlord/verify-phone": LandlordDashboard,
  "/landlord/verify": LandlordDashboard,
  "/landlord/analytics": LandlordDashboard,
  "/landlord/payments": LandlordDashboard,
  "/landlord/calendar": LandlordDashboard,
  "/landlord/wallet": LandlordDashboard,
  "/landlord/notifications": LandlordDashboard,
  "/landlord/billing": LandlordDashboard,
  "/landlord/profile": LandlordDashboard,
  "/landlord/security": LandlordDashboard,
  "/landlord/team": LandlordDashboard,
  "/landlord/bulk": LandlordDashboard,
  "/landlord/api-keys": LandlordDashboard,
  "/landlord/services": LandlordDashboard,
  "/landlord/support": LandlordDashboard,
  
  // Admin portal (authenticated admin only)
  "/portal-8k2m9x/dashboard": AdminDashboard,
  "/portal-8k2m9x/analytics": AdminDashboard,
  "/portal-8k2m9x/landlords": AdminDashboard,
  "/portal-8k2m9x/properties": AdminDashboard,
  "/portal-8k2m9x/ownership-review": AdminDashboard,
  "/portal-8k2m9x/tenants": AdminDashboard,
  "/portal-8k2m9x/reports": AdminDashboard,
  "/portal-8k2m9x/tenancies": AdminDashboard,
  "/portal-8k2m9x/users": AdminDashboard,
  "/portal-8k2m9x/teams": AdminDashboard,
  "/portal-8k2m9x/subscriptions": AdminDashboard,
  "/portal-8k2m9x/discounts": AdminDashboard,
  "/portal-8k2m9x/promo-codes": AdminDashboard,
  "/portal-8k2m9x/payments": AdminDashboard,
  "/portal-8k2m9x/support-tickets": AdminDashboard,
  "/portal-8k2m9x/disputes": AdminDashboard,
  "/portal-8k2m9x/equifax-reporting": AdminDashboard,
  "/portal-8k2m9x/payment-fees": AdminDashboard,
  "/portal-8k2m9x/fee-revenue": AdminDashboard,
  "/portal-8k2m9x/fee-payouts": AdminDashboard,
  "/portal-8k2m9x/wallets": AdminDashboard,
  "/portal-8k2m9x/admin-team": AdminDashboard,
  "/portal-8k2m9x/email-templates": AdminDashboard,
  "/portal-8k2m9x/newsletter": AdminDashboard,
  "/portal-8k2m9x/client-documents": AdminDashboard,
  "/portal-8k2m9x/audit-logs": AdminDashboard,
  "/portal-8k2m9x/settings": AdminDashboard,
  "/portal-8k2m9x/consent-requests": AdminDashboard,
  "/portal-8k2m9x/api-keys": AdminDashboard,
  "/portal-8k2m9x/careers": AdminDashboard,
  "/portal-8k2m9x/contacts": AdminDashboard,
  "/portal-8k2m9x/waitlist": AdminDashboard,
};

// Dynamic route patterns
const dynamicRoutes = [
  { pattern: /^\/careers\/[^/]+$/, component: JobPage },
  { pattern: /^\/apply\/[^/]+$/, component: ApplyPage },
  { pattern: /^\/fix\/[^/]+$/, component: FixPage },
  { pattern: /^\/demo$/, component: DemoPage },
  { pattern: /^\/contact$/, component: ContactPage },
  { pattern: /^\/reference\/[^/]+$/, component: ReferencePage },
  { pattern: /^\/onboard\/roommate\/[^/]+$/, component: RoommateIdPage },
  { pattern: /^\/onboard\/[^/]+$/, component: OnboardPage },
  { pattern: /^\/landlord\/properties\/[^/]+\/verify$/, component: LandlordDashboard },
  { pattern: /^\/landlord\/properties\/[^/]+$/, component: LandlordDashboard },
  { pattern: /^\/landlord\/disputes\/[^/]+$/, component: LandlordDashboard },
  { pattern: /^\/landlord\/tenants\/[^/]+\/end$/, component: LandlordDashboard },
  { pattern: /^\/landlord\/tenants\/[^/]+$/, component: LandlordDashboard },
  { pattern: /^\/landlord\/reports\/[^/]+\/edit$/, component: LandlordDashboard },
  { pattern: /^\/landlord\/reports\/[^/]+\/new$/, component: LandlordDashboard },
];

// Protected route prefixes
const protectedPrefixes = ["/landlord", "/portal-8k2m9x", "/careers/portal"];

// Get current route component
function getRouteComponent(path) {
  
  // Admin login is public
  if (path === '/portal-8k2m9x/login') {
    return AdminLoginPage;
  }
  // Admin invite acceptance is public (new staff set their password)
  if (path === '/portal-8k2m9x/accept-invite') {
    return AcceptStaffInvitePage;
  }
  
  // Check for protected routes
  const isProtectedRoute = protectedPrefixes.some(prefix => path.startsWith(prefix));
  
  if (isProtectedRoute && !isAuthenticated()) {
    // Redirect to appropriate login
    if (path.startsWith("/portal-8k2m9x")) {
      window.location.href = "/portal-8k2m9x/login";
    } else if (path.startsWith("/careers")) {
      window.location.href = "/careers/login";
    } else {
      window.location.href = "/login";
    }
    return null;
  }
  
  // Check for admin routes
  if (path.startsWith("/portal-8k2m9x")) {
    const user = getCurrentUser();
    if (!user || user.role !== "admin") {
      window.location.href = "/portal-8k2m9x/login";
      return null;
    }
  }
  
  // Check for landlord routes
  if (path.startsWith("/landlord")) {
    const user = getCurrentUser();
    if (!user || user.role !== "landlord") {
      window.location.href = "/login";
      return null;
    }
  }

  if (path.startsWith("/careers/portal")) {
    const user = getCurrentUser();
    if (!user || user.role !== "candidate") {
      window.location.href = "/careers/login";
      return null;
    }
  }
  
  // Find matching route (exact match first, then dynamic routes, then prefix match)
  let Component = routes[path];
  
  if (!Component) {
    // Try dynamic route patterns
    for (const { pattern, component } of dynamicRoutes) {
      if (pattern.test(path)) {
        Component = component;
        break;
      }
    }
  }
  
  if (!Component) {
    // Try prefix matching for other dynamic routes
    for (const [routePath, RouteComponent] of Object.entries(routes)) {
      if (path.startsWith(routePath.replace(/\/:[^/]+/g, ''))) {
        Component = RouteComponent;
        break;
      }
    }
  }
  
  // Default to home if no match
  if (!Component) {
    Component = HomePage;
  }
  
  return Component;
}

function App() {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const sync = () => setPath(window.location.pathname);
    window.addEventListener('popstate', sync);
    window.addEventListener(NAV_EVENT, sync);
    const uninstall = installLinkInterceptor();
    return () => { window.removeEventListener('popstate', sync); window.removeEventListener(NAV_EVENT, sync); uninstall(); };
  }, []);

  const RouteComponent = getRouteComponent(path);
  
  if (!RouteComponent) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner h-8 w-8"></div>
      </div>
    );
  }
  
  return (
    <div className="App">
      <RouteComponent />
      <Toaster 
        position="bottom-center"
        expand={false}
        richColors={false}
        closeButton={false}
        duration={3000}
        toastOptions={{
          style: {
            background: '#1e293b',
            color: '#f8fafc',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
          className: 'professional-toast',
        }}
      />
    </div>
  );
}

export default App;
